import { useState } from 'react';
import { portalApi } from '../api/client';
import type { SupplierSubmission } from '../api/client';
import { useApi } from '../hooks/useApi';
import { T, SectionHeader, Card, Chip, Skeleton, ErrorBox } from '../components/ui';

const STATUS_COLOR: Record<string, string> = {
  pending:        T.amber,
  approved:       T.sage,
  rejected:       T.crimson,
  needs_revision: '#e87a3a',
};

const TYPE_COLOR: Record<string, string> = {
  certification:  '#4aa8a0',
  lot_data:       '#4f8ec9',
  sensor_device:  '#8b6fd4',
  company_update: T.sub,
};

export default function SupplierPortalPage() {
  const [activeTab,  setActiveTab]  = useState<'queue' | 'approved'>('queue');
  const [selected,   setSelected]   = useState<SupplierSubmission | null>(null);
  const [deciding,   setDeciding]   = useState(false);
  const [note,       setNote]       = useState('');
  const [message,    setMessage]    = useState('');

  const { data: pendingData, loading: pLoading, error: pError, refetch: refetchPending } =
    useApi(() => portalApi.review.list('pending'));
  const { data: approvedData, loading: aLoading } =
    useApi(() => portalApi.review.list('approved'));

  const pending  = pendingData?.submissions  || [];
  const approved = approvedData?.submissions || [];
  const shown    = activeTab === 'queue' ? pending : approved;

  const decide = async (status: 'approved' | 'rejected' | 'needs_revision') => {
    if (!selected) return;
    setDeciding(true);
    try {
      await portalApi.review.decide(selected.id, status, note || undefined);
      setMessage(`Submission ${status}`);
      setSelected(null);
      setNote('');
      refetchPending();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Action failed');
    }
    setDeciding(false);
  };

  const renderPayload = (payload: unknown) => {
    try {
      const obj = typeof payload === 'string' ? JSON.parse(payload) : payload;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Object.entries(obj as Record<string, unknown>).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 12, fontSize: 11, borderBottom: `1px solid ${T.border}`, paddingBottom: 5 }}>
              <span style={{ color: T.sub, minWidth: 120, textTransform: 'uppercase', letterSpacing: .5, fontSize: 9, fontWeight: 600, marginTop: 1 }}>{k.replace(/_/g, ' ')}</span>
              <span style={{ color: T.text, fontFamily: typeof v === 'string' && v.length > 30 ? "'DM Mono', monospace" : 'inherit', fontSize: typeof v === 'string' && v.length > 30 ? 10 : 11 }}>
                {String(v)}
              </span>
            </div>
          ))}
        </div>
      );
    } catch {
      return <span style={{ color: T.sub, fontSize: 11 }}>{String(payload)}</span>;
    }
  };

  return (
    <div className="fade-up">
      <SectionHeader
        title="Supplier Portal"
        sub={`${pending.length} submission${pending.length !== 1 ? 's' : ''} awaiting review`}
        right={
          pending.length > 0
            ? <Chip label={`${pending.length} Pending`} color={T.amber} dot />
            : <Chip label="Queue clear" color={T.sage} />
        }
      />

      {message && (
        <div style={{ background: `${T.sage}12`, border: `1px solid ${T.sage}30`, borderRadius: 8, padding: '10px 16px', marginBottom: 18, color: T.sage, fontSize: 12 }}>
          {message}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['queue', 'approved'] as const).map(t => (
          <button key={t} onClick={() => { setActiveTab(t); setSelected(null); }} style={{
            background: activeTab === t ? `${T.copper}18` : 'transparent',
            border: `1px solid ${activeTab === t ? T.copper + '40' : T.border}`,
            color: activeTab === t ? T.copper : T.sub,
            borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: activeTab === t ? 700 : 400,
          }}>
            {t === 'queue' ? `Review Queue (${pending.length})` : `Approved (${approved.length})`}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {/* List */}
        <div>
          {(activeTab === 'queue' ? pLoading : aLoading)
            ? [1,2,3].map(i => <div key={i} style={{ marginBottom: 10 }}><Skeleton h={72} /></div>)
            : pError
              ? <ErrorBox message={pError} onRetry={refetchPending} />
              : shown.length === 0
                ? <div style={{ color: T.sub, fontSize: 12, textAlign: 'center', padding: '40px 0' }}>
                    {activeTab === 'queue' ? 'No pending submissions — queue is clear.' : 'No approved submissions yet.'}
                  </div>
                : shown.map(s => (
                  <div key={s.id} onClick={() => setSelected(selected?.id === s.id ? null : s)} style={{
                    background: selected?.id === s.id ? T.lift : T.panel,
                    border: `1px solid ${selected?.id === s.id ? T.copper + '50' : T.border}`,
                    borderLeft: `3px solid ${TYPE_COLOR[s.submission_type] || T.sub}`,
                    borderRadius: 10, padding: '12px 15px', marginBottom: 8,
                    cursor: 'pointer', transition: 'all .12s',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                        {(s as any).supplier_name || 'Unknown Supplier'}
                      </span>
                      <Chip label={s.status.replace('_', ' ').toUpperCase()} color={STATUS_COLOR[s.status] || T.sub} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Chip label={s.submission_type.replace('_', ' ')} color={TYPE_COLOR[s.submission_type] || T.sub} />
                      <span style={{ fontSize: 10, color: T.dim }}>
                        {new Date(s.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))
          }
        </div>

        {/* Detail + review panel */}
        <div>
          {!selected
            ? (
              <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🏭</div>
                <div style={{ color: T.sub, fontSize: 12 }}>Select a submission to review its payload and approve, reject, or request revisions.</div>
              </Card>
            )
            : (
              <Card style={{ borderLeft: `3px solid ${TYPE_COLOR[selected.submission_type] || T.sub}` }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 8 }}>
                    {(selected as any).supplier_name || 'Supplier Submission'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                    <Chip label={selected.submission_type.replace('_', ' ')} color={TYPE_COLOR[selected.submission_type] || T.sub} />
                    <Chip label={selected.status.replace('_', ' ').toUpperCase()} color={STATUS_COLOR[selected.status] || T.sub} />
                    <span style={{ fontSize: 10, color: T.dim, alignSelf: 'center' }}>
                      {new Date(selected.created_at).toLocaleString()}
                    </span>
                  </div>

                  {/* Payload */}
                  <div style={{ background: T.card, borderRadius: 9, padding: '12px 14px', marginBottom: 16 }}>
                    <div style={{ fontSize: 9, color: T.sub, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 10 }}>Submission Data</div>
                    {renderPayload(selected.payload)}
                  </div>

                  {selected.reviewer_notes && (
                    <div style={{ background: `${T.amber}0a`, border: `1px solid ${T.amber}25`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 11, color: T.sub }}>
                      <span style={{ color: T.amber, fontWeight: 600 }}>Reviewer note: </span>
                      {selected.reviewer_notes}
                    </div>
                  )}
                </div>

                {/* Review actions — only show for pending */}
                {selected.status === 'pending' && (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', color: T.sub, fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 6 }}>
                        Reviewer note (optional)
                      </label>
                      <textarea
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder="Add context for the supplier…"
                        rows={3}
                        style={{ width: '100%', background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: '9px 12px', color: T.text, fontSize: 12, resize: 'vertical', outline: 'none', fontFamily: "'DM Sans', sans-serif" }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => decide('approved')} disabled={deciding} style={{
                        flex: 1, background: `${T.sage}15`, border: `1px solid ${T.sage}40`, color: deciding ? T.sub : T.sage,
                        borderRadius: 8, padding: '10px', cursor: deciding ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700,
                      }}>
                        {deciding ? '…' : '✓ Approve'}
                      </button>
                      <button onClick={() => decide('needs_revision')} disabled={deciding} style={{
                        flex: 1, background: `${T.amber}10`, border: `1px solid ${T.amber}35`, color: deciding ? T.sub : T.amber,
                        borderRadius: 8, padding: '10px', cursor: deciding ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700,
                      }}>
                        Needs Revision
                      </button>
                      <button onClick={() => decide('rejected')} disabled={deciding} style={{
                        background: `${T.crimson}10`, border: `1px solid ${T.crimson}30`, color: deciding ? T.sub : T.crimson,
                        borderRadius: 8, padding: '10px 14px', cursor: deciding ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700,
                      }}>
                        Reject
                      </button>
                    </div>
                  </>
                )}
              </Card>
            )
          }
        </div>
      </div>
    </div>
  );
}
